import { useState, useEffect, useCallback, useMemo } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import {
  Search,
  Mail,
  Calendar,
  Download,
  Users,
} from "lucide-react";
import { adminApi } from "../../lib/adminApi";

export const AdminNewsletter = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchSubscribers = useCallback(async () => {
    try {
      const response = await adminApi.get("/admin/newsletter");
      setSubscribers(response.data.subscribers);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Failed to fetch subscribers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const filteredSubscribers = useMemo(
    () => subscribers.filter((sub) => sub.email?.toLowerCase().includes(searchTerm.toLowerCase())),
    [subscribers, searchTerm]
  );

  const thisMonthCount = useMemo(() => {
    const now = new Date();
    return subscribers.filter((s) => {
      const d = new Date(s.subscribed_at);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
  }, [subscribers]);

  const thisWeekCount = useMemo(() => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return subscribers.filter((s) => new Date(s.subscribed_at) >= weekAgo).length;
  }, [subscribers]);

  const sanitizeCSVCell = (value) => {
    const s = String(value);
    if (/^[=+\-@\t\r]/.test(s)) return `'${s}`;
    return s;
  };

  const exportToCSV = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," +
      "Email,Subscribed Date\n" +
      subscribers
        .map((sub) => `${sanitizeCSVCell(sub.email)},${sanitizeCSVCell(new Date(sub.subscribed_at).toLocaleDateString())}`)
        .join("\n");

    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", "newsletter_subscribers.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Exported successfully!");
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-48"></div>
        <div className="h-12 bg-gray-200 rounded"></div>
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-900 uppercase">
            Newsletter Subscribers
          </h1>
          <p className="text-gray-600 mt-1">
            {total} total subscribers
          </p>
        </div>
        <Button onClick={exportToCSV} className="bg-brand-navy text-white">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Stats Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Subscribers</p>
              <p className="text-2xl font-bold text-gray-900">{total}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Mail className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">This Month</p>
              <p className="text-2xl font-bold text-gray-900">
                {thisMonthCount}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-6 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">This Week</p>
              <p className="text-2xl font-bold text-gray-900">
                {thisWeekCount}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <Card className="p-4 bg-white border-0 shadow-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search by email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </Card>

      {/* Subscribers List */}
      <Card className="bg-white border-0 shadow-sm overflow-hidden">
        {filteredSubscribers.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No subscribers found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-6 py-4">
                    #
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-6 py-4">
                    Email Address
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-6 py-4">
                    Subscribed Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSubscribers.map((subscriber, index) => (
                  <tr key={subscriber.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4">
                      <a
                        href={`mailto:${subscriber.email}`}
                        className="text-gray-900 hover:text-brand-orange"
                      >
                        {subscriber.email}
                      </a>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(subscriber.subscribed_at).toLocaleDateString()} at{" "}
                      {new Date(subscriber.subscribed_at).toLocaleTimeString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
